import React, { useEffect, useState } from 'react';
import {
    Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { WatchlistChangeSection } from '../ImportDiffDetails';
import SkippedUploadRowsTable, { hasRowsToFix, notConsideredRows, notConsideredText } from './SkippedUploadRows';
import { isDuplicateSecurityRow } from '../../utils/watchlistDuplicateSecurity';

const rowsIn = (details, bucket) => {
    const items = details?.[bucket];
    return (Array.isArray(items) ? items : Object.values(items || {}))
        .map((item) => item?.after || item?.before || item)
        .filter((row) => row && typeof row === 'object');
};

// A completed result uses the same immutable diff rows as the staged review.
// It has no approval action and never starts another Watchlist operation.
export default function WatchlistCompletedResultDialog({ result, onClose }) {
    const [unchangedOpen, setUnchangedOpen] = useState(false);
    useEffect(() => { setUnchangedOpen(false); }, [result]);
    const details = result?.diff_details_json;
    const notAdded = [
        ...rowsIn(details, 'unresolved_excluded'),
        ...rowsIn(details, 'inactive_excluded'),
    ];
    const unchanged = rowsIn(details, 'retained');
    const skipped = notConsideredRows(result?.input_validation);
    return (
        <Dialog open={Boolean(result)} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>No new Watchlist version was created</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ mb: 2 }}>
                    {!notAdded.length
                        ? 'The Watchlist already matches this input.'
                        : notAdded.every(isDuplicateSecurityRow)
                            ? 'No securities were added. They are already on your Watchlist.'
                            : 'No securities were added. Review Security Resolution for details.'}
                </Typography>
                {result?.note && <Typography sx={{ mb: 2 }}>{result.note}</Typography>}
                {notAdded.length ? <WatchlistChangeSection
                    title={`Not added (${notAdded.length})`}
                    rows={notAdded}
                    options={{ showReason: true, headerColor: 'error.main',
                        tableAriaLabel: 'Securities not added' }}
                /> : <Typography variant="subtitle2">Not added (0)</Typography>}
                <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setUnchangedOpen((value) => !value)}
                        aria-expanded={unchangedOpen}
                        endIcon={unchangedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                        {`Unchanged (${unchanged.length})`}
                    </Button>
                    <Collapse in={unchangedOpen} unmountOnExit>
                        <WatchlistChangeSection title="Unchanged" rows={unchanged}
                            options={{ showTitle: false, tableAriaLabel: 'Unchanged Watchlist securities' }} />
                    </Collapse>
                </Box>
                {skipped.total > 0 && <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Rows not considered ({skipped.total})</Typography>
                    {hasRowsToFix(result?.input_validation) && <Typography variant="body2" sx={{ mb: 1 }}>
                        {notConsideredText({ ...skipped, shown: skipped.rows.length })}
                    </Typography>}
                    <SkippedUploadRowsTable rows={skipped.rows} />
                </Box>}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
}
